import { usePageModels } from '@/context/PageModels';
import { isEqual } from 'lodash';
import React, { useRef } from 'react';
import { useHistory } from 'react-router-dom';
import { appendScript } from '@/utils/append-script';
import { MarketoForm, MktoForms2 } from './Marketo.types';

export type MarketoContextValue = {
  marketo?: MktoForms2;
  isLoading?: boolean;
  loadForm: (props: {
    formId: string;
    onSuccess?: (
      form?: MarketoForm,
      defaultValues?: Record<string, any>,
      buttonText?: string,
    ) => void;
  }) => Promise<MarketoForm | undefined>;
};

export type MarketoProviderProps = {
  children: React.ReactNode;
};

export const MarketoContext = React.createContext<MarketoContextValue>({
  loadForm: () => Promise.reject(),
});

export const MarketoProvider = ({ children }: React.PropsWithChildren<any>) => {
  const {
    config: { marketo: { munchkin_id: munchkinId = '', base_url: baseUrl = '' } = {} },
  } = usePageModels();
  const [marketo, setMarketo] = React.useState<MktoForms2 | undefined>(window.MktoForms2);
  const waitFor = React.useRef<((mkto: MktoForms2) => void)[]>([]);
  const [isLoading, setIsLoading] = React.useState(!document.querySelector('#marketo-forms2'));
  const history = useHistory();

  const handleRedirection = React.useCallback(
    (url = '') => {
      const parsedURL = new URL(url);

      if (
        window.location.protocol !== parsedURL.protocol ||
        window.location.host !== parsedURL.host
      ) {
        window.location.href = url;
        return;
      }

      // The URL gotten is the same as the current ones. <May be an unexpected Marketo behavior>.
      if (
        window.location.origin === parsedURL.origin &&
        window.location.pathname === parsedURL.pathname
      ) {
        return;
      }

      const formattedURL = parsedURL.pathname + (parsedURL.hash.length > 1 ? parsedURL.hash : '');
      history.push(formattedURL);
    },
    [history],
  );

  const loadForm = React.useCallback<MarketoContextValue['loadForm']>(
    ({ formId, onSuccess }) => {
      if (!marketo && !isLoading) return Promise.resolve(undefined);

      /**
       * If the form is already loaded, resolve it.
       * If Marketo instance is loading, wait for it to load the form.
       * If Marketo instance is not loaded, resolve the promise with undefined.
       * If Marketo instance is loaded, load the form.
       */
      return new Promise<MarketoForm | undefined>((resolve) => {
        if (!marketo) return resolve(undefined);

        return marketo.loadForm(baseUrl, munchkinId, formId, resolve);
      }).then((form) => {
        console.log('MarketoProvider loadForm', { form });
        if (!form) return undefined;

        let defaultValues = form.getValues();

        form.onSuccess((_, thankYouURL = '') => {
          onSuccess?.(form, defaultValues.current);

          if (thankYouURL) {
            setTimeout(() => {
              handleRedirection(thankYouURL);
            }, 500);
          }

          return false;
        });

        // eslint-disable-next-line no-param-reassign
        form.getDefaultValues = () => ({ ...defaultValues });
        // eslint-disable-next-line no-param-reassign
        form.setDefaultValues = (values: Record<string, string>) => {
          const currentValues = form.getValues();

          if (isEqual(currentValues, values)) return;

          form.setValues(values);
          defaultValues = values;
        };

        return form;
      });
    },
    [marketo, isLoading, baseUrl, munchkinId, handleRedirection],
  );

  React.useEffect(() => {
    /**
     * Return early if marketo already exist
     */
    if (marketo) return;

    /**
     * Callback to run when the Marketo script is loaded.
     * Resolves any promises waiting for the Marketo instance to load.
     * Sets the Marketo instance and updates the isLoading state.
     */
    const onScriptLoad = () => {
      console.log('script loaded', window.MktoForms2, waitFor.current);
      if (waitFor.current.length > 0) {
        waitFor.current.forEach((cb) => cb(window.MktoForms2));
        waitFor.current = [];
      }

      setMarketo(window.MktoForms2);
      setIsLoading(false);
    };

    /**
     * Load the Marketo script.
     */
    try {
      appendScript({
        src: `${baseUrl}/js/forms2/js/forms2.min.js`,
        onLoad: onScriptLoad,
        once: true,
        id: 'marketo-forms2',
      });
    } catch (error) {
      console.error('Error loading Marketo script', error);
    }
  }, [marketo, baseUrl]);

  return (
    <MarketoContext.Provider value={{ marketo, isLoading, loadForm }}>
      {children}
    </MarketoContext.Provider>
  );
};

export const useMarketo = () => {
  const context = React.useContext(MarketoContext);

  if (!context) {
    throw new Error('useMarketo must be used within a MarketoProvider');
  }

  return context;
};
